/**
 * Hides dropdown element
 * @param {HTMLElement|null} dropdownElement - Dropdown DOM element
 */
export function hideDropdown(dropdownElement) {
    if (dropdownElement) {
        dropdownElement.classList.add('hidden');
    }
}

/**
 * Formats volume bytes to MB or GB
 * @param {number} volumeBytes - Volume in bytes
 * @returns {string} Formatted volume (e.g., "500 MB" or "1.5 GB")
 */
export function formatVolume(volumeBytes) {
    const volumeGB = (volumeBytes / (1024 * 1024 * 1024));
    return volumeGB < 1
        ? `${(volumeGB * 1024).toFixed(0)} MB`
        : `${volumeGB.toFixed(1)} GB`;
}

/**
 * Formats date string to locale string with 24-hour format
 * @param {string} dateString - ISO date string or '-'
 * @returns {string} Formatted date string or '-'
 */
export function formatDate(dateString) {
    if (!dateString || dateString === '-') {
        return '-';
    }
    try {
        const date = new Date(dateString);
        return date.toLocaleString(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    } catch (e) {
        return dateString;
    }
}

/**
 * Normalizes text by removing accents and special characters for search
 * @param {string} text - Text to normalize
 * @returns {string} Normalized lowercase text
 */
export function normalizeText(text) {
    return text.toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s]/g, '');
}

import i18n from './i18n.js';

/**
 * Translates "Day"/"Days" to Spanish or ensures proper pluralization in English
 * @param {string} text - Text to translate
 * @param {number} count - Number to determine if plural (optional, defaults to checking text)
 * @returns {string} Translated text
 */
export function translateDayText(text, count = null) {
    if (!text) return text;
    
    const isSpanish = i18n.getLanguage() === 'es';
    const isPlural = count !== null ? count > 1 : /\bDays\b/i.test(text);
    text = text.replace(/(\S)(Day|Days)\b/gi, '$1 $2');
    if (isSpanish) {
        if (isPlural) {
            text = text.replace(/\bDays\b/gi, 'Días').replace(/\bDay\b/gi, 'Días');
        } else {
            text = text.replace(/\bDays\b/gi, 'Días').replace(/\bDay\b/gi, 'Días');
        }
    } else {
        if (isPlural) {
            text = text.replace(/\bDay\b(?!s\b)/gi, 'Days');
        }
    }
    
    return text;
}

/**
 * Calcula y ajusta el background-size del body para que la imagen
 * siempre ocupe el 100% del ancho, y si es necesario, se agrande
 * para alcanzar 400px de altura
 */
export function adjustBodyBackgroundSize() {
    const body = document.body;
    const backgroundImage = getComputedStyle(body).backgroundImage;
    
    // Extraer la URL de la imagen (remover "url(" y ")" o "url('...')")
    const urlMatch = backgroundImage.match(/url\(['"]?([^'"]+)['"]?\)/);
    if (!urlMatch || !urlMatch[1]) {
        console.warn('No se encontró imagen de fondo en el body');
        return;
    }
    
    const imageUrl = urlMatch[1];
    
    // Crear una imagen para obtener sus dimensiones naturales
    const img = new Image();
    img.onload = function() {
        const viewportWidth = window.innerWidth;
        const targetHeight = 500; // Altura objetivo de 400px
        
        // Calcular la relación de aspecto de la imagen
        const imageAspectRatio = img.width / img.height;
        
        // Calcular la altura que tendría la imagen con 100% de ancho
        const heightAt100PercentWidth = viewportWidth / imageAspectRatio;
        
        let currentHeight;
        
        // Si con 100% de ancho la altura es menor a 400px, necesitamos agrandar
        if (heightAt100PercentWidth < targetHeight) {
            // Calcular el ancho necesario para alcanzar 400px de altura
            const requiredWidth = targetHeight * imageAspectRatio;
            const widthPercentage = (requiredWidth / viewportWidth) * 100;
            body.style.backgroundSize = `${widthPercentage}% auto`;
            currentHeight = targetHeight; // La altura será 400px
        } else {
            // Si con 100% de ancho ya alcanza o supera 400px de altura, usar 100% auto
            body.style.backgroundSize = '100% auto';
            currentHeight = heightAt100PercentWidth; // La altura será la calculada
        }
                
        // Obtener la altura del top-menu y restarla del currentHeight
        const topMenu = document.querySelector('.top-menu');
        let topMenuHeight = 0;
        if (topMenu) {
            topMenuHeight = topMenu.offsetHeight;
        }
        
        // Calcular la altura final restando la altura del menu
        const finalHeight = Math.max(0, Math.round(currentHeight) - topMenuHeight);
        
        // Establecer una variable CSS para usar en el CSS
        body.style.setProperty('--image-height', `${finalHeight}px`);
    };
    
    img.onerror = function() {
        console.warn('No se pudo cargar la imagen de fondo para calcular dimensiones');
        // Fallback al tamaño original si hay error
        body.style.backgroundSize = '100% auto';
    };
    
    img.src = imageUrl;
}

